import lodash, { get, pick } from 'lodash'
import React, { ReactElement, useState } from 'react'
import { connect } from 'react-redux'
import { Route } from 'react-router-dom'
import { createStructuredSelector } from 'reselect'

import By1stLogFooter from '../../../1stlog-ui/src/components/By1stLogFooter'
import ErrorBoundary from '../../../1stlog-ui/src/components/ErrorBoundary'
import RouterContextProvider from '../../../1stlog-ui/src/components/RouterContextProvider'
import { TranslationContextProvider } from '../../../1stlog-ui/src/components/Translations'
import IdleTimer from '../../../shipmetrics-shared/components/IdleTimer'
import {
  SettingsContextProvider,
  useSettings,
} from '../../../shipmetrics-shared/components/Settings'
import {
  CustomerContext,
  CustomerContextProvider,
} from '../../components/Customer'
import { Content, Root } from '../../components/Layout'
import AddressBookNew from '../AddressBookNew'
import CronOrdersList from '../CronOrdersList'
import CronOrder from '../CronOrdersList/CronOrder'
import CustomsList from '../Customs'
import Deliverylist from '../Deliverylist'
import Export from '../Export'
import HandOverList from '../HandOverList'
import Interfaces from '../Interfaces'
import Order from '../Order'
import Success from '../Order/Success'
import { PaginatedOrderJobs } from '../OrderJobs'
import Orders from '../OrderList'
import Detail from '../OrderList/Detail'
import PrinterContextProvider from '../PrinterContextProvider'
import Stammdaten from '../Stammdaten'
import Templates from '../Templates'
import TemplateEditor from '../Templates/TemplateEditor'
import CustomerChangeNavigator from './CustomerChangeNavigator'
import Topbar from './Topbar'
import { AppContext } from './useApp'

const connectRedux = connect(createStructuredSelector({}))

const IdleTimerWrapper = (): ReactElement => {
  const { settings: { loginTimeout = 0 } = {} } = useSettings()
  const timeOutDuration = Number(loginTimeout) * 60 * (1 / 3)
  const timeUntilLogout = Number(loginTimeout) * 60 * (2 / 3)
  return (
    <IdleTimer
      timeOutDuration={timeOutDuration}
      timeUntilLogout={timeUntilLogout}
    />
  )
}

const App = (): ReactElement => {
  const [ref, setRef] = useState<HTMLDivElement | null>(null)

  const handleRef = (ref: HTMLDivElement | null): void => {
    setRef(ref)
  }

  const scrollToTop = (): void => {
    setTimeout(() => {
      try {
        if (ref) {
          ref.scrollTop = 0
        }
      } catch (err) {
        //
      }
    }, 100)
  }

  return (
    <AppContext.Provider
      value={{
        scrollToTop,
      }}
    >
      <RouterContextProvider>
        <CustomerContextProvider>
          <SettingsContextProvider
            initialSettings={pick(global?.settings, [
              'loginTimeout',
              'printer_id',
            ])}
            defaultLocalSettings={{ loginTimeout: 0 }}
            ignoreLanguage
          >
            <TranslationContextProvider
              projects={['auftrag', 'shipmetrics_shared', '1stlog_ui']}
              i18n={get(global, 'i18n')}
              tenantId={get(global, 'currentUser.tenant_id')}
              initialLanguage={get(global, 'currentUser.language')}
              useWiwo
              languageFiles={[
                'shipmetrics-auftrag.translations.json',
                'shipmetrics-shared.translations.json',
                '1stlog-ui.translations.json',
              ]}
            >
              <PrinterContextProvider>
                <Root>
                  <Topbar />
                  <IdleTimerWrapper />
                  <TemplateEditor />
                  <CustomerChangeNavigator />
                  <CustomerContext.Consumer>
                    {({
                      currentCustomer: { id: currentCustomerId },
                      currentCustomer,
                    }) => (
                      <Content
                        id="App/Content"
                        className={currentCustomerId}
                        ref={handleRef}
                        key={currentCustomerId}
                      >
                        <ErrorBoundary>
                          <Route path="/" exact component={Order} />
                          <Route
                            path="/quickorder/:presetId"
                            component={Order}
                          />
                          <Route path="/templates" component={Templates} />
                          <Route
                            path="/stammdaten"
                            exact
                            component={Stammdaten}
                          />
                          <Route
                            path="/orders"
                            exact
                            component={(p) => (
                              <Orders
                                {...p}
                                cronOrdersEnabled={lodash.get(
                                  currentCustomer,
                                  'cronOrdersEnabled',
                                  false,
                                )}
                              />
                            )}
                          />
                          <Route
                            path="/handovers"
                            exact
                            component={HandOverList}
                          />
                          <Route
                            path="/interfaces"
                            exact
                            component={Interfaces}
                          />
                          {lodash.get(
                            currentCustomer,
                            'cronOrdersEnabled',
                            false,
                          ) && (
                            <Route
                              path="/cronorders"
                              exact
                              component={CronOrdersList}
                            />
                          )}

                          <Route
                            path="/addressbook"
                            exact
                            component={AddressBookNew}
                          />
                          <Route
                            path="/deliverylist"
                            exact
                            component={Deliverylist}
                          />
                          <Route path="/export/:type" component={Export} />
                          <Route path="/success" component={Success} />
                          <Route path="/cronorder/:id" component={CronOrder} />
                          <Route path="/order/:id" component={Detail} />
                          <Route path="/customs" component={CustomsList} />
                          {lodash.get(
                            currentCustomer,
                            'massOrderUploadEnabled',
                          ) && (
                            <Route
                              path="/order-jobs"
                              component={PaginatedOrderJobs}
                            />
                          )}
                        </ErrorBoundary>
                      </Content>
                    )}
                  </CustomerContext.Consumer>
                  <By1stLogFooter />
                </Root>
              </PrinterContextProvider>
            </TranslationContextProvider>
          </SettingsContextProvider>
        </CustomerContextProvider>
      </RouterContextProvider>
    </AppContext.Provider>
  )
}

export default connectRedux(App)
